import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RightOutlined } from "@ant-design/icons";
import { App, Layout, Spin } from "antd";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { ProFooter, ProTag } from "./proFooter.js";
import { useStores } from "../store/stores.js";
import { __, _i } from "../utils/i18n.js";
const { Header, Content, Footer } = Layout;
const SettingsContent = observer(()=>{
    const { message } = App.useApp();
    const { optionStore } = useStores();
    const [thumbnailFolder, setThumbnailFolder] = useState(optionStore.others.thumbnailFolder);
    const [thumbnailFilename, setThumbnailFilename] = useState(optionStore.others.thumbnailFilename);
    const [chunkSize, setChunkSize] = useState(optionStore.others.chunkSize);
    const [verify, setVerify] = useState(undefined);
    const { isPro, showProHints } = optionStore.others;
    const handleVerify = useCallback(async ()=>{
        if (!thumbnailFolder && !thumbnailFilename) {
            setVerify(undefined);
            return true;
        }
        const verifyResult = await optionStore.verify(thumbnailFolder, thumbnailFilename);
        setVerify(verifyResult);
        return verifyResult.errors.length === 0;
    }, [
        optionStore,
        thumbnailFolder,
        thumbnailFilename,
        chunkSize
    ]);
    const handleSave = useCallback(async ()=>{
        /* Silence is golden... Until you have PRO Version! */ try {
            await optionStore.update(thumbnailFolder, thumbnailFilename, chunkSize);
            message.success(__("Settings saved successfully!"));
        } catch (e) {
            message.error(e.responseJSON.message);
        }
    }, [
        optionStore,
        thumbnailFolder,
        thumbnailFilename,
        chunkSize,
        handleVerify
    ]);
    return /*#__PURE__*/ _jsx(Layout, {
        children: /*#__PURE__*/ _jsxs(Spin, {
            spinning: optionStore.updateBusy,
            children: [
                /*#__PURE__*/ _jsxs(Header, {
                    style: {
                        background: "#fff"
                    },
                    children: [
                        __("Thumbnails"),
                        " ",
                        /*#__PURE__*/ _jsx(RightOutlined, {}),
                        " ",
                        __("Settings")
                    ]
                }),
                /*#__PURE__*/ _jsx(Content, {
                    style: {
                        margin: "24px 16px 0"
                    },
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "rtg-dialog-content",
                        style: {
                            display: "block"
                        },
                        children: [
                            !isPro && showProHints && /*#__PURE__*/ _jsx(ProFooter, {}),
                            /*#__PURE__*/ _jsx("h2", {
                                children: __("Thumbnail folder and file name")
                            }),
                            __("You can configure the file path of the thumbnails and thereby optimize them for SEO. The following variables can be used in the folder and file name:"),
                            /*#__PURE__*/ _jsxs("ul", {
                                children: [
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%id%"
                                            }),
                                            ": ",
                                            __("ID of the attachment (e.g. 998)")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%size-identifier%"
                                            }),
                                            ":",
                                            " ",
                                            __('Name of the thumbnail size (e.g. "medium", "large")')
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%identifier-width%"
                                            }),
                                            ": ",
                                            __("Width of the registered thumbnail size (in px)")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%identifier-height%"
                                            }),
                                            ":",
                                            " ",
                                            __("Height of the registered thumbnail size (in px)")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%name%"
                                            }),
                                            ":",
                                            " ",
                                            __('Basename of the uploaded file (e.g. "car" for filename "car.jpg")')
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%extension%"
                                            }),
                                            ":",
                                            " ",
                                            __('Extension of the file (e.g. "jpg" for filename "car.jpg")')
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%image-width%"
                                            }),
                                            ": ",
                                            __("Width of the generated thumbnail (in px)")
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("li", {
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: "%image-height%"
                                            }),
                                            ": ",
                                            __("Height of the generated thumbnail (in px)")
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("h4", {
                                children: [
                                    __("Thumbnail subfolder"),
                                    " ",
                                    /*#__PURE__*/ _jsx(ProTag, {
                                        feature: "option-thumbnail-subfolder"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                value: thumbnailFolder,
                                disabled: !isPro,
                                onChange: (e)=>setThumbnailFolder(e.target.value),
                                className: "regular-text"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                children: __("Folder where thumbnails should be stored inside wp-content/uploads/. By default, the thumbnails are not stored in a subfolder.")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rtg-example-container",
                                children: [
                                    __("Examples"),
                                    ":",
                                    /*#__PURE__*/ _jsxs("ul", {
                                        children: [
                                            /*#__PURE__*/ _jsx("li", {
                                                children: _i(__("{{code}}thumbnails/%%size-identifier%%{{/code}} can result in: thumbnails/{{strong}}medium{{/strong}}"), {
                                                    code: /*#__PURE__*/ _jsx("code", {}),
                                                    strong: /*#__PURE__*/ _jsx("strong", {})
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx("li", {
                                                children: _i(__("{{code}}%%identifier-width%%x%%identifier-height%%/{{/code}} can result in: {{strong}}300x300{{/strong}}"), {
                                                    code: /*#__PURE__*/ _jsx("code", {}),
                                                    strong: /*#__PURE__*/ _jsx("strong", {})
                                                })
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("h4", {
                                children: [
                                    __("Thumbnail filename"),
                                    " ",
                                    /*#__PURE__*/ _jsx(ProTag, {
                                        feature: "option-thumbnail-filename"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                value: thumbnailFilename,
                                disabled: !isPro,
                                onChange: (e)=>setThumbnailFilename(e.target.value),
                                className: "regular-text"
                            }),
                            " ",
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                children: __("File name for thumbnails generated dynamically. Please leave empty to use the default file name in WordPress.")
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rtg-example-container",
                                children: [
                                    __("Example"),
                                    ":",
                                    /*#__PURE__*/ _jsx("ul", {
                                        children: /*#__PURE__*/ _jsx("li", {
                                            children: _i(__("{{code}}%%name%%-%%image-width%%x%%image-height%%.%%extension%%{{/code}} can result in: {{strong}}car-300x300.jpg{{/strong}}"), {
                                                code: /*#__PURE__*/ _jsx("code", {}),
                                                strong: /*#__PURE__*/ _jsx("strong", {})
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: /*#__PURE__*/ _jsx("button", {
                                    className: "button",
                                    disabled: !(thumbnailFilename || thumbnailFolder),
                                    onClick: handleVerify,
                                    children: __("Preview thumbnail upload structure")
                                })
                            }),
                            !!verify && /*#__PURE__*/ _jsx("div", {
                                children: Object.keys(verify.errors).length > 0 ? /*#__PURE__*/ _jsx("div", {
                                    className: "notice inline notice-error notice-alt",
                                    style: {
                                        margin: "0 0 10px"
                                    },
                                    children: /*#__PURE__*/ _jsx("p", {
                                        children: Object.values(verify.errors).join(". ")
                                    })
                                }) : /*#__PURE__*/ _jsx("div", {
                                    className: "notice inline notice-success notice-alt",
                                    style: {
                                        margin: "0 0 10px"
                                    },
                                    children: /*#__PURE__*/ _jsxs("ul", {
                                        children: [
                                            /*#__PURE__*/ _jsx("li", {
                                                children: /*#__PURE__*/ _jsx("strong", {
                                                    children: __("Examples of your Thumbnail folder and file name:")
                                                })
                                            }),
                                            Object.values(verify.pathes).map((path, idx)=>/*#__PURE__*/ _jsx("li", {
                                                    children: path
                                                }, idx))
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                children: __("Chunk size")
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "number",
                                min: "1",
                                value: chunkSize,
                                onChange: (e)=>setChunkSize(+e.target.value),
                                className: "regular-text"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "description",
                                children: __("Thumbnail regeneration is performed sequentially in chunks to avoid overloading your server. If you notice too high CPU usage, you can reduce the chunk size.")
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Footer, {
                    style: {
                        textAlign: "center"
                    },
                    children: /*#__PURE__*/ _jsx("button", {
                        className: "button-primary",
                        onClick: handleSave,
                        children: __("Save")
                    })
                })
            ]
        })
    });
});
export { SettingsContent };
